/**
 * PartnerAccessBanner — persistent top banner shown whenever a PARTNER user has
 * switched into one of their CUSTOMER accounts (via `switchToCustomerAccount`).
 * Gives a one-tap "Exit" back to the partner's own account, so they don't have
 * to dig through Settings → Switch account.
 *
 * Exports two components (mirrors SnapshotViewBanner):
 *   - `PartnerAccessBannerHost`: wraps the app, renders the banner at the top
 *     when in a customer account, and zeroes the top safe-area inset for its
 *     children so screens below don't double-pad.
 *   - `PartnerAccessBanner` (default): just the bar itself — used by the host.
 *
 * The host sits above `NavigationContainer` so the banner stays visible across
 * every screen while in the customer account. Tapping "Exit" calls
 * `switchBackToPrimaryAccount()`, which AppShell observes and re-mounts the
 * partner shell.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import {
  SafeAreaView,
  SafeAreaInsetsContext,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { useSelector } from 'react-redux';
import { useTheme } from '../hooks/useTheme';
import { useAppDispatch, type RootState } from '../store';
import Icon from './Icon';
import { switchBackToPrimaryAccount } from '../store/actions/accounts';
import type { AppTheme } from '../theme';

const BANNER_BG = '#1E3A8A'; // blue-900 — distinct from the snapshot amber

/**
 * True when the active account is a customer account the partner accessed via
 * partner membership (not their own/home account). The primary-exists +
 * different-id checks avoid a false positive on fresh login before `primary`
 * is hydrated. Mirrors the detection in SettingsScreen.
 */
function usePartnerAccessView() {
  const current = useSelector((s: RootState) => (s as any).accounts?.current);
  const primary = useSelector((s: RootState) => (s as any).accounts?.primary);
  return {
    active:
      !!current?.via_partner_access &&
      !!primary?.account_id &&
      primary.account_id !== current?.account_id,
    customerName: current?.name || current?.first_name || null,
  };
}

// ─── Banner bar ───────────────────────────────────────────────────────────────

function PartnerAccessBanner() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { active, customerName } = usePartnerAccessView();
  const [exiting, setExiting] = useState(false);

  const styles = useMemo(() => makeStyles(theme), [theme]);

  const handleExit = useCallback(async () => {
    if (exiting) return;
    setExiting(true);
    try {
      await dispatch(switchBackToPrimaryAccount() as any);
    } finally {
      setExiting(false);
    }
  }, [dispatch, exiting]);

  if (!active) return null;

  return (
    <SafeAreaView edges={['top']} style={styles.safeArea}>
      <View style={styles.bar}>
        <View style={styles.iconWrap}>
          <Icon icon={['fad', 'user-shield']} size={14} color="#fff" />
        </View>
        <View style={styles.textWrap}>
          <Text style={styles.title} numberOfLines={1}>
            {customerName ? `Viewing ${customerName}'s account` : 'Viewing a customer account'}
          </Text>
          <Text style={styles.subtitle} numberOfLines={1}>
            You're in partner access — tap Exit to return to your account
          </Text>
        </View>
        <TouchableOpacity
          style={styles.exitBtn}
          onPress={handleExit}
          activeOpacity={0.8}
          disabled={exiting}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          {exiting ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <>
              <Text style={styles.exitBtnText}>Exit</Text>
              <Icon icon={['fad', 'arrow-right-from-bracket']} size={11} color="#fff" />
            </>
          )}
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}

// ─── Host ─────────────────────────────────────────────────────────────────────

interface HostProps {
  children: React.ReactNode;
}

export function PartnerAccessBannerHost({ children }: HostProps) {
  const { active } = usePartnerAccessView();
  const insets = useSafeAreaInsets();

  if (!active) return <>{children}</>;

  const childInsets = { ...insets, top: 0 };

  return (
    <View style={hostStyles.root}>
      <PartnerAccessBanner />
      <SafeAreaInsetsContext.Provider value={childInsets}>
        <View style={hostStyles.children}>
          {children}
        </View>
      </SafeAreaInsetsContext.Provider>
    </View>
  );
}

export default PartnerAccessBanner;

// ─── Styles ───────────────────────────────────────────────────────────────────

const hostStyles = StyleSheet.create({
  root: { flex: 1 },
  children: { flex: 1 },
});

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    safeArea: {
      backgroundColor: BANNER_BG,
    },
    bar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: 8,
      backgroundColor: BANNER_BG,
    },
    iconWrap: {
      width: 24,
      height: 24,
      borderRadius: 12,
      backgroundColor: 'rgba(255,255,255,0.18)',
      justifyContent: 'center',
      alignItems: 'center',
    },
    textWrap: { flex: 1 },
    title: {
      color: '#fff',
      fontSize: theme.font.size.sm,
      fontWeight: theme.font.weight.semibold,
    },
    subtitle: {
      color: 'rgba(255,255,255,0.82)',
      fontSize: theme.font.size.xs,
      marginTop: 1,
    },
    exitBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: theme.radius.full,
      backgroundColor: 'rgba(0,0,0,0.2)',
    },
    exitBtnText: {
      color: '#fff',
      fontSize: theme.font.size.xs,
      fontWeight: theme.font.weight.semibold,
    },
  });
